export default {
  // A quiet neutral canvas keeps the default shell's controls and generated
  // cards in the foreground. Workspace record vars and chrome.stage still
  // take precedence through the existing stage chain.
  stage: ['milk', 'gray2'],
  // Ambient cards sit one quiet fill step above the canvas in either theme.
  // Solid skins and explicit workspace card fills keep their own surfaces.
  cardGlass: ['white.72', 'gray5.45'],
  // THE AMBIENT SKY INK, quieter than the brand (DESIGN-WL-BOOT-LOADER-SKY-1,
  // Nika 2026-09-27: "for dashboard img bg keep opacity lower"). The brand pair
  // is ['gray1.25', 'white.15'] (HOME_AMBIENT_SKIN_SPEC §2); the shell's own
  // design system wins over the brand library's for the same key, so
  // AmbientBackdrop keeps `background: 'stageInk'` and paints this pair.
  // The boot overlay keeps its independent loading treatment.
  stageInk: ['gray1.08', 'white.04'],
  // The loader's mountain range (DESIGN-WL-BOOT-LOADER-MOTION-1): the same
  // hues, stronger — the range is the progress, not a backdrop. The
  // pre-bundle overlay mirrors it (#040404 .2 light, #fff .16 dark).
  loaderInk: ['gray1.2', 'white.16'],
  // The loader's ground (DESIGN-WL-BOOT-LOADER-MOTION-1, Nika 2026-09-29:
  // "loading should be black or white depends on theme mode"): the brand's
  // own white (light) and black (dark). The pre-bundle overlay mirrors it.
  loaderGround: ['white', 'black'],
  // Calendar-specific: adaptive day selection highlight.
  //
  // DOT FORM, no space, no `--` prefix. The legacy `['--blue .12', '--blue
  // .18']` spelling mints NOTHING: scratch's colour grammar is `blue.12`, and
  // a space before the alpha fails `parseColorToken`, so the atomic class is
  // emitted with an EMPTY body and the tint paints invisibly — the framework
  // logs "[scratch] color token `blue .12` has a space before its alpha/tone
  // modifier … Dropped rather than shipped invisible" on EVERY page load of
  // the shell, because this file is the shell's own design system. Measured
  // and re-pinned by __tests__/colorTokenGrammar.test.js.
  selectedDay: ['blue.12', 'blue.18'],
  // Brand cyan (#00FFFF) has ~1.25:1 contrast on white — effectively invisible
  // in light mode. Override as adaptive: bright cyan in dark, dark teal in
  // light (#0088A8 achieves ≥4.5:1 on white while staying recognisably cyan).
  cyan: ['#00FFFF', '#0088A8'],
  // §4.2 danger ink — the accentInk pattern for the danger role. Brand `red`
  // (#EB6650) passes on the dark stage (5.7:1 on gray2) but measured 2.82:1
  // on the light stage (needs 4.5 at control sizes — /my Danger zone, AA walk
  // 2026-08-08). The light half deepens the same hue to ≥5.0:1 on every light
  // stage tint (5.69 white / 5.04 gray15 / 5.00 overlay-on-stage).
  // [lightValue, darkValue].
  // Dark slot inlined for the same deployed-runtime reason as the poster
  // catalog below: 'red' aliased the BRAND red (#EB6650) but the published
  // scratch passes the string through, and the browser paints CSS keyword
  // red (#FF0000) instead — a hotter, off-brand danger ink on every deployed
  // dark surface.
  redInk: ['#B93A24', '#EB6650' /* brand red */],

  // ── HOME-BOARD POSTER CATALOG (tickets/fable.md HOME-POSTER-BRAND-1) ──────
  //
  // ⚠️ LITERAL HEXES, NOT TOKEN-NAME ALIASES — deployed-runtime compat.
  // These were authored as aliases into the brand palette
  // (`posterTickets: 'orange'`, `posterChat: 'grassgreen-35'`, …). The REPO
  // scratch resolves a string value recursively (system/color.js, the
  // `isString(val)` re-lookup), so locally — where node_modules/@symbo.ls/*
  // symlink to the repo — every alias resolved and the board looked right.
  // The PUBLISHED scratch the CI bundle actually ships has no such branch:
  // the alias string landed in CSS verbatim. Aliases that happen to be CSS
  // keywords painted as the KEYWORD (`orange` → rgb(255,165,0), not brand
  // orange #E98232; same for yellow/purple/brown); every other alias
  // (`electricBlue`, `red-40`, `grassgreen-35`, `gray2`, …) was an invalid
  // CSS colour and the declaration dropped — transparent, unstyled cards.
  // Measured live on dev.my.symbols.app/w/system/hq 2026-08-20, reported by
  // Nika as "colors, backgrounds and styles got broken".
  //
  // Each hex below is the value the REPO pipeline resolves the original
  // alias to — computed through the FULL repo pipeline (createConfig + set +
  // resolveColorRgb, the same harness homePosterTokens.test.js uses; a bare
  // getColor over raw tables loses the tone hop and five values came out
  // wrong, which that test caught) — so the rendered result is byte-identical to
  // the feature as designed — only the SHAPE is now one the published
  // runtime supports. Plain hex strings are passthrough on every runtime.
  // The original alias is kept beside each value; when the smbls publish
  // unblocks and the alias-resolving scratch ships, these MAY go back to
  // aliases — but only after the served bundle proves it resolves them.
  // The ink pairs stay `white`/`black`: CSS-native keywords, passthrough
  // everywhere, and proven painting correctly on the same live page.
  // One SEMANTIC pair per poster family — the hue tiles on the home board
  // (Studio / Tasks / Chat / Calendar / …) and every launcher tile, row
  // re-plating and hover wash that belongs to the same identity. Resolved by
  // `functions/homeWidgetHue.js` (`HOUSE_HUE_MAP` points at THESE tokens, not
  // at the raw brand hues), so a poster's whole identity changes in one
  // place. The direction itself is unchanged (Nika 2026-08-16 "keep it,
  // improve, but also maintain the custom workspace branded ones"): one
  // fixed hue + binary ink, identical in dark and light — posters are
  // THEME-INVARIANT by construction, which is why every pair below is a flat
  // hue and an ink whose two halves are equal.
  //
  // WHY TOKENS AND NOT RAW HUES IN THE MAP — the per-workspace override
  // channel. scratch emits each entry as `--color-poster<Family>` /
  // `--color-poster<Family>Ink` on the `:root` rule. A BRANDED workspace
  // supplies its own poster pairs through the record channel the shell
  // already has — `Workspace.settings.designSystem.vars` — which
  // `functions/workspaceConfigs.js` `_applyDesignSystem` writes INLINE on
  // `<html>` (inline beats the `:root` rule; `_revertDesignSystem` removes it
  // on a workspace switch). A workspace without those vars renders exactly
  // the defaults below. Zero tenant values live in code; no second channel.
  //   record contract (values are plain CSS colours, supply the PAIR):
  //     "--color-posterStudio":    "#1F4FD8"      ← the poster's hue
  //     "--color-posterStudioInk": "#FFFFFF"      ← its binary ink (white|black)
  //   families: Studio Tickets Chat Calendar Reminders Meet Broadcast
  //             Documents Agents Team Analytics People Health Files
  //
  // The INK tokens are PAIR-shaped on purpose (two equal halves), not flat:
  // scratch resolves a flat token's `.NN` alpha to a BAKED `rgba(...)` at
  // build time, but a pair token's alpha to `rgb(from var(--color-x) r g b /
  // .NN)` — read off the var at PAINT time — so the secondary-ink steps
  // (`posterStudioInk.81` …) follow a record override too. Byte-identical
  // defaults: the pair's computed colour is the same white/black as before.
  //
  // Hue choices: the brand hue each family always had, except three deepened
  // with the `−N` grammar design-direction.md §6 names for exactly this (the
  // `green-20` Meet precedent) so WHITE ink clears ≥5.0:1 (the §8.5 margin
  // over the 4.5:1 AA text floor) — measured against this design system
  // (`__tests__/homePosterTokens.test.js` re-derives every number):
  //   calendar `red` 3.21 → `red-40` 5.20 · chat `grassgreen` 3.30 →
  //   `grassgreen-35` 5.31 · meet `green-20` 3.61 → `green-45` 5.03.
  posterStudio: '#0F45FF' /* electricBlue */,
  posterStudioInk: ['white', 'white'],
  posterTickets: '#E98232' /* orange */,
  posterTicketsInk: ['black', 'black'],
  posterChat: '#477732' /* grassgreen-35 */,
  posterChatInk: ['white', 'white'],
  posterCalendar: '#C33E28' /* red-40 */,
  posterCalendarInk: ['white', 'white'],
  posterReminders: '#E8CC56' /* yellow */,
  posterRemindersInk: ['black', 'black'],
  posterMeet: '#2C7F29' /* green-45 */,
  posterMeetInk: ['white', 'white'],
  posterBroadcast: '#FFA4D1' /* pink+20 */,
  posterBroadcastInk: ['black', 'black'],
  posterDocuments: '#55B7D8' /* indigoblue */,
  posterDocumentsInk: ['black', 'black'],
  posterAgents: '#7345AF' /* purple */,
  posterAgentsInk: ['white', 'white'],
  posterTeam: '#1EDAFF' /* cerulean+30 */,
  posterTeamInk: ['black', 'black'],
  posterAnalytics: '#141416' /* gray2 */,
  posterAnalyticsInk: ['white', 'white'],
  posterPeople: '#7D6755' /* brown */,
  posterPeopleInk: ['white', 'white'],
  posterHealth: '#BCD835' /* phosphorus */,
  posterHealthInk: ['black', 'black'],
  posterFiles: '#00BCFF' /* cerulean */,
  posterFilesInk: ['black', 'black']
}
